import React from 'react';
import { Download, FileCheck2, X, ShieldCheck, CalendarRange, CreditCard } from 'lucide-react';
import { downloadSubscriptionInvoicePdf, fallbackInvoiceNumber, formatInvoiceDate, formatInvoiceMoney } from '../../utils/subscriptionInvoicePdf';
import './SubscriptionInvoice.css';

const SubscriptionInvoice = ({ subscription, user, onClose }) => {
    if (!subscription) return null;
    const customer = subscription.customerSnapshot || {};
    const invoiceNumber = subscription.invoiceNumber || fallbackInvoiceNumber(subscription);
    const total = subscription.totalPaidSnapshot ?? subscription.priceSnapshot ?? 0;
    const subtotal = subscription.subtotalSnapshot ?? total;
    const tax = subscription.taxSnapshot ?? 0;
    const gstPercentage = subscription.gstPercentageSnapshot ?? 0;
    const currency = subscription.currency || 'INR';
    const address = [customer.addressLine1, customer.addressLine2, customer.city, customer.district, customer.state, customer.country]
        .filter(Boolean).join(', ') || 'India';

    return (
        <div className="subscription-invoice-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <section className="subscription-invoice-dialog" role="dialog" aria-modal="true" aria-labelledby="subscription-invoice-title">
                <div className="subscription-invoice-toolbar">
                    <div><FileCheck2 size={18} /><span>Invoice preview</span></div>
                    <div>
                        <button type="button" onClick={() => downloadSubscriptionInvoicePdf(subscription, user)}><Download size={16} /> Download PDF</button>
                        <button type="button" className="subscription-invoice-close" onClick={onClose} aria-label="Close invoice"><X size={19} /></button>
                    </div>
                </div>

                <div className="subscription-invoice-sheet">
                    <header className="subscription-invoice-brand">
                        <div className="subscription-invoice-mark">G</div>
                        <div className="subscription-invoice-brand-copy">
                            <h2 id="subscription-invoice-title">Gettimelam <em>Matrimony</em></h2>
                            <p>Thoughtful connections. Lasting relationships.</p>
                        </div>
                        <div className="subscription-invoice-number">
                            <span>Payment invoice</span>
                            <strong>{invoiceNumber}</strong>
                            <small>{formatInvoiceDate(subscription.purchasedAt)}</small>
                        </div>
                    </header>

                    <div className="subscription-invoice-parties">
                        <article><span>Sold by</span><h3>HUNTSWORLD</h3><p><strong>GSTIN: 34EWSPM5689F1ZQ</strong></p><p>info@huntsworld.com</p></article>
                        <article><span>Bill to</span><h3>{customer.name || user?.name || user?.businessName || 'Member'}</h3><p>{address}</p><p>{customer.email || user?.email}</p><p>{customer.mobile || user?.mobile}</p></article>
                    </div>

                    <div className="subscription-invoice-meta">
                        <div><ShieldCheck size={17} /><span>Payment<strong>{subscription.paymentStatus === 'paid' ? 'Paid' : subscription.paymentStatus === 'gifted' ? 'Gifted by admin' : (subscription.paymentStatus || 'Recorded')}</strong></span></div>
                        <div><CreditCard size={17} /><span>Order ID<strong>{subscription.paymentOrderId || '—'}</strong></span></div>
                        <div><FileCheck2 size={17} /><span>Payment ID<strong>{subscription.paymentId || '—'}</strong></span></div>
                    </div>

                    <div className="subscription-invoice-table">
                        <div className="subscription-invoice-table-head"><span>Item description</span><span>Validity</span><span>Qty.</span><span>Total</span></div>
                        <div className="subscription-invoice-table-row">
                            <span><strong>{subscription.planNameSnapshot} plan</strong><small>Gettimelam membership subscription</small></span>
                            <span><CalendarRange size={15} /> {formatInvoiceDate(subscription.purchasedAt)} – {formatInvoiceDate(subscription.expiresAt)}</span>
                            <span>1</span><span>{formatInvoiceMoney(total, currency)}</span>
                        </div>
                    </div>

                    <div className="subscription-invoice-bottom">
                        <div className="subscription-invoice-support"><h4>Payment & support</h4><p>Payment received securely. This invoice is generated electronically and does not require a physical signature.</p><span>Questions? info@huntsworld.com</span></div>
                        <div className="subscription-invoice-totals">
                            <p><span>Sub total</span><strong>{formatInvoiceMoney(subtotal, currency)}</strong></p>
                            <p><span>GST ({gstPercentage}%)</span><strong>{formatInvoiceMoney(tax, currency)}</strong></p>
                            <div><span>Total paid</span><strong>{formatInvoiceMoney(total, currency)}</strong></div>
                        </div>
                    </div>

                    <footer className="subscription-invoice-footer">
                        <div aria-hidden="true" />
                        <h4>Thank you for choosing Gettimelam Matrimony.</h4>
                        <p>HUNTSWORLD <span>•</span> info@huntsworld.com <span>•</span> GSTIN 34EWSPM5689F1ZQ</p>
                        <small>Gettimelam Matrimony is a Huntsworld service</small>
                        <small>This is a computer-generated payment invoice and does not require a physical signature.</small>
                    </footer>
                </div>
            </section>
        </div>
    );
};

export default SubscriptionInvoice;
